import {
  BaseEventService,
  type ServiceActivationContext,
} from '@realitycollective/service-framework';
import { createLogger } from '../../util/logger';
import { AssistantRoster, type AssistantEntry } from '../../core/assistantRoster';
import { RoomTopics } from '../../protocol/roomTopics';
import {
  ASSIST_LIVE,
  ASSIST_PAUSED,
  parseAssistantPresence,
  wearerStateJson,
  type WearerAssistState,
} from '../../protocol/presenceMessages';
import type { ILiveKitRoomService } from './ILiveKitRoomService';
import type { AssistantRosterEventMap, IAssistantRosterService } from './IAssistantRosterService';

const log = createLogger('Roster');

export type AssistantRosterConfig = Record<string, never>;

/**
 * Feeds the pure `AssistantRoster` reducer from room events + the `presence`
 * topic, applies D5's subscription toggles through the room service, and
 * broadcasts the wearer's `wearerState` hints on join/change so consoles
 * always know what the wearer chose. Port of `AssistantRosterService.cs`.
 */
export class AssistantRosterService
  extends BaseEventService<AssistantRosterEventMap, AssistantRosterConfig>
  implements IAssistantRosterService
{
  private readonly room: ILiveKitRoomService;
  private readonly roster = new AssistantRoster();
  private readonly unsubs: Array<() => void> = [];

  /** Empty = assist off (no broadcast, subscriptions untouched). */
  private assistToken = '';

  /** The wearer's standing choice about hearing assistants. */
  private _assistantsMuted = false;

  constructor(context: ServiceActivationContext<AssistantRosterConfig>, room: ILiveKitRoomService) {
    super(context);
    this.room = room;
  }

  get assistants(): readonly AssistantEntry[] {
    return this.roster.entries;
  }

  get count(): number {
    return this.roster.count;
  }

  get assistantsMuted(): boolean {
    return this._assistantsMuted;
  }

  override start(): void {
    this.unsubs.push(
      this.roster.onJoined((entry) => this.onRosterJoined(entry)),
      this.roster.onLeft((entry) => this.emit('assistantLeft', entry)),
      this.roster.onChanged((entry) => this.emit('assistantChanged', entry)),
      this.room.on('participantJoined', (participant) =>
        this.roster.applyParticipantJoined(participant.identity, participant.name, participant.metadata),
      ),
      this.room.on('participantLeft', (participant) => {
        if (this.roster.applyParticipantLeft(participant.identity)) this.broadcastWearerState();
      }),
      this.room.on('remoteTrackChanged', (change) => this.onRemoteTrackChanged(change)),
      this.room.on('data', (message) => {
        if (message.topic !== RoomTopics.presence) return;
        // Only assistant presence parses here; the wearer's own broadcast and
        // future message types fall through harmlessly (additive rule).
        const presence = parseAssistantPresence(message.json);
        if (presence) this.roster.applyPresence(presence);
      }),
      this.room.on('stateChange', (state) => {
        if (state === 'failed' || state === 'closed') this.roster.clear();
      }),
    );
  }

  override destroy(): void {
    this.unsubs.forEach((unsub) => unsub());
    this.unsubs.length = 0;
    super.destroy();
  }

  setHidden(identity: string, hidden: boolean): void {
    if (!this.roster.setHidden(identity, hidden)) return;

    // Local subscription toggle (D5). While paused everything is already
    // unsubscribed - the choice is remembered and re-applied on resume by
    // setAssistState.
    if (!this.isPaused) this.room.setTrackSubscribed(identity, 'video', !hidden);

    log.info(`${identity} video ${hidden ? 'hidden' : 'shown'} by wearer`);
    this.broadcastWearerState();
  }

  setMuted(identity: string, muted: boolean): void {
    if (!this.applyMuted(identity, muted)) return;
    log.info(`${identity} audio ${muted ? 'muted' : 'unmuted'} by wearer`);
    this.broadcastWearerState();
    this.refreshAssistantsMuted();
  }

  setAllMuted(muted: boolean): void {
    // Identity snapshot: muting raises change events whose subscribers read
    // the roster, so never iterate the live entries while mutating.
    const identities = this.roster.entries.map((entry) => entry.identity);

    // Recorded BEFORE anything is applied, so a handler woken by the first
    // mute cannot read a stale answer. It is remembered even with nobody to
    // apply it to - the choice is what the next assistant to join inherits.
    const choiceChanged = this._assistantsMuted !== muted;
    this._assistantsMuted = muted;

    let changed = false;
    for (const identity of identities) changed = this.applyMuted(identity, muted) || changed;

    if (changed) this.broadcastWearerState();

    if (choiceChanged) {
      log.info(`assistant audio ${muted ? 'muted' : 'unmuted'} by wearer (${identities.length} in session)`);
      this.emit('allMutedChanged', muted);
    }
  }

  toggleAllMuted(): void {
    this.setAllMuted(!this._assistantsMuted);
  }

  setAssistState(token: string): void {
    const next = token ?? '';
    if (this.assistToken === next) return;
    this.assistToken = next;

    if (this.isPaused) {
      // Paused: all assistant audio/video unsubscribed (Plan-01 §2.2) -
      // panels collapse, nothing flows down.
      for (const entry of this.roster.entries) {
        this.room.setTrackSubscribed(entry.identity, 'video', false);
        this.room.setTrackSubscribed(entry.identity, 'audio', false);
      }
    } else if (this.assistToken === ASSIST_LIVE) {
      // Resume: each assistant returns to the wearer's own choices.
      for (const entry of this.roster.entries) {
        this.room.setTrackSubscribed(entry.identity, 'video', !entry.hiddenByWearer);
        this.room.setTrackSubscribed(entry.identity, 'audio', !entry.mutedByWearer);
      }
    }

    this.broadcastWearerState();
  }

  private get isPaused(): boolean {
    return this.assistToken === ASSIST_PAUSED;
  }

  /** The mute half of `setMuted`, without the log line, broadcast or aggregate refresh. */
  private applyMuted(identity: string, muted: boolean): boolean {
    if (!this.roster.setMuted(identity, muted)) return false;
    if (!this.isPaused) this.room.setTrackSubscribed(identity, 'audio', !muted);
    return true;
  }

  /**
   * Keep the standing choice honest after a PER-ASSISTANT toggle: the
   * hand-menu tile claims to describe whether the wearer can hear the
   * assistant, so unmuting the only muted one has to clear it.
   */
  private refreshAssistantsMuted(): void {
    const entries = this.roster.entries;
    if (entries.length === 0) return; // nothing to derive from - keep the standing choice
    const allMuted = entries.every((entry) => entry.mutedByWearer);
    if (allMuted === this._assistantsMuted) return;
    this._assistantsMuted = allMuted;
    this.emit('allMutedChanged', allMuted);
  }

  private onRosterJoined(entry: AssistantEntry): void {
    // A standing mute applies to whoever arrives next: the wearer muted the
    // role, not the person. Applied BEFORE the join is announced so the first
    // panel render is already correct.
    if (this._assistantsMuted) this.applyMuted(entry.identity, true);
    this.emit('assistantJoined', entry);
    this.broadcastWearerState();
  }

  private onRemoteTrackChanged(change: {
    readonly identity: string;
    readonly kind: 'video' | 'audio';
    readonly published: boolean;
    readonly muted: boolean;
  }): void {
    this.roster.applyTrackChange(change.identity, change.kind === 'audio', change.published, change.muted);

    // Hide/mute (and pause) must survive an assistant re-publish: a fresh
    // publication auto-subscribes, so the wearer's standing choice is
    // re-applied the moment it appears.
    if (!change.published) return;
    const entry = this.roster.get(change.identity);
    if (!entry) return;
    if (change.kind === 'video' && (entry.hiddenByWearer || this.isPaused)) {
      this.room.setTrackSubscribed(change.identity, 'video', false);
    } else if (change.kind === 'audio' && (entry.mutedByWearer || this.isPaused)) {
      this.room.setTrackSubscribed(change.identity, 'audio', false);
    }
  }

  /**
   * Broadcast the wearer's state + per-assistant hints (Plan-01 §2.2). Only
   * while assistance is active - the wire defines live|paused and nothing
   * else - and only with a connected room.
   */
  private broadcastWearerState(): void {
    if (!this.assistToken || !this.room.isRoomConnected) return;
    this.room.publishData(
      RoomTopics.presence,
      wearerStateJson(this.assistToken as WearerAssistState, this.roster.wearerHints()),
    );
  }
}
